/**
 * DataTable component - Display conjunction data in sortable, filterable table
 *
 * Features:
 * - Sortable columns (click header to sort)
 * - Export to CSV functionality
 * - CDM-specific formatting: Pc scientific notation, risk color coding,
 *   relative TCA, emergency row highlighting
 * - Responsive design with horizontal scroll
 * - Row selection for CDM data with Plan Maneuver action
 * - Default sort by Pc descending for CDM data
 */

import { useState, useMemo, type ReactNode } from 'react'
import { ArrowUpDown, ArrowUp, ArrowDown, Download } from 'lucide-react'
import PlanManeuverButton from './PlanManeuverButton'
import {
  isPcColumn,
  isMissDistanceColumn,
  isTcaColumn,
  formatPc,
  formatMissDistance,
  formatRelativeTca,
  getRiskLevel,
  riskColor,
  isEmergencyReportable,
  sortCdmColumns,
} from './cdmFormatters'

// Column definition
interface Column {
  key: string
  label: string
  sortable?: boolean
  format?: (value: unknown) => string
  /** If set, cell text color will use this function */
  colorFn?: (value: unknown) => string | undefined
}

interface DataTableProps {
  data: Record<string, unknown>[] // Flexible data structure
  title?: string
  onPlanManeuver?: (cdmId: string) => void // Callback for maneuver planning
  disabled?: boolean // Disable interactive features
}

type SortDirection = 'asc' | 'desc' | null

// Helper: Format column label (snake_case -> Title Case)
function formatColumnLabel(key: string): string {
  return key
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}

// Helper: Infer formatter based on key name and value type
function inferFormatter(
  key: string,
  value: unknown,
  isCdm: boolean
): { format?: (value: unknown) => string; colorFn?: (value: unknown) => string | undefined } {
  // CDM-specific formatters take priority
  if (isCdm) {
    if (isPcColumn(key)) {
      return {
        format: formatPc,
        colorFn: (val: unknown) => riskColor(getRiskLevel(val)),
      }
    }

    if (isMissDistanceColumn(key)) {
      return { format: formatMissDistance }
    }

    if (isTcaColumn(key)) {
      return { format: formatRelativeTca }
    }
  }

  // Date/time fields (non-CDM or non-TCA date fields)
  if (key.includes('created') || key.includes('time') || (!isCdm && key.includes('tca'))) {
    return {
      format: (val: unknown) => {
        if (!val) return '-'
        try {
          const date = new Date(String(val))
          return date.toLocaleString('en-US', {
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
          })
        } catch {
          return String(val)
        }
      },
    }
  }

  // Non-CDM probability fields (show as percentage)
  if (!isCdm && (key.includes('probability') || key === 'pc')) {
    return {
      format: (val: unknown) => {
        if (val == null) return '-'
        const num = Number(val)
        if (isNaN(num)) return String(val)
        return `${(num * 100).toExponential(2)}%`
      },
    }
  }

  // Distance fields (non-CDM)
  if (!isCdm && (key.includes('distance') || key.includes('rng'))) {
    return {
      format: (val: unknown) => {
        if (val == null) return '-'
        const num = Number(val)
        if (isNaN(num)) return String(val)
        return `${(num / 1000).toFixed(2)} km`
      },
    }
  }

  // Boolean fields
  if (typeof value === 'boolean') {
    return { format: (val: unknown) => (val ? 'Yes' : 'No') }
  }

  // Number fields (format with commas)
  if (typeof value === 'number') {
    return {
      format: (val: unknown) => {
        if (val == null) return '-'
        const num = Number(val)
        if (isNaN(num)) return String(val)
        return num.toLocaleString('en-US', { maximumFractionDigits: 2 })
      },
    }
  }

  return {}
}

// Helper: Format cell value (fallback)
function formatCellValue(value: unknown): string {
  if (value == null) return '-'
  if (typeof value === 'object') return JSON.stringify(value)
  return String(value)
}

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v)
}

// Helper: render a cell value, expanding arrays-of-objects into nested tables.
// Falls back to formatCellValue for primitives and irregular structures.
function renderCellValue(value: unknown): ReactNode {
  if (Array.isArray(value) && value.length > 0 && value.every(isPlainObject)) {
    const rows = value as Record<string, unknown>[]
    const columns: string[] = []
    const seen = new Set<string>()
    for (const row of rows) {
      for (const k of Object.keys(row)) {
        if (!seen.has(k)) {
          seen.add(k)
          columns.push(k)
        }
      }
    }
    return (
      <table className="text-[10px] font-mono border border-dark-200 rounded">
        <thead>
          <tr className="bg-dark-100 text-dark-500">
            {columns.map((col) => (
              <th
                key={col}
                className="px-2 py-1 text-left font-semibold border-b border-dark-200 whitespace-nowrap"
              >
                {col}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} className={i % 2 === 0 ? 'bg-dark-50' : 'bg-dark-100/40'}>
              {columns.map((col) => (
                <td
                  key={col}
                  className="px-2 py-1 align-top whitespace-nowrap"
                >
                  {formatCellValue(row[col])}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    )
  }
  return formatCellValue(value)
}

// Helper: Convert data to CSV format
function convertToCSV(data: Record<string, unknown>[], columns: Column[]): string {
  const header = columns.map((col) => `"${col.label}"`).join(',')
  const rows = data.map((row) =>
    columns
      .map((col) => {
        const value = row[col.key]
        if (value == null) return '""'
        const formatted = col.format ? col.format(value) : formatCellValue(value)
        return `"${String(formatted).replace(/"/g, '""')}"`
      })
      .join(',')
  )
  return [header, ...rows].join('\n')
}

// Helper: Get CDM ID from a row
function getCdmId(row: Record<string, unknown>): string | null {
  const id = row.cdm_id || row.id || row.CDM_ID || row.MESSAGE_ID
  return id ? String(id) : null
}

// Helper: Detect if data is CDM data
function isCdmDataCheck(data: Record<string, unknown>[]): boolean {
  if (data.length === 0) return false
  const firstRow = data[0]
  return (
    'cdm_id' in firstRow ||
    'CDM_ID' in firstRow ||
    'MESSAGE_ID' in firstRow ||
    'TCA' in firstRow ||
    'tca' in firstRow ||
    'COLLISION_PROBABILITY' in firstRow ||
    'collision_probability' in firstRow
  )
}

function DataTable({ data, title, onPlanManeuver, disabled = false }: DataTableProps) {
  const hasData = data && data.length > 0
  const isCdmData = useMemo(() => hasData && isCdmDataCheck(data), [data, hasData])

  // Default sort: Pc descending for CDM data
  const [sortKey, setSortKey] = useState<string | null>(() => {
    if (!hasData || !isCdmData) return null
    const pcKey = Object.keys(data[0]).find(isPcColumn)
    return pcKey || null
  })
  const [sortDirection, setSortDirection] = useState<SortDirection>(() => {
    if (!hasData || !isCdmData) return null
    const pcKey = Object.keys(data[0]).find(isPcColumn)
    return pcKey ? 'desc' : null
  })
  const [selectedRowIndex, setSelectedRowIndex] = useState<number | null>(null)

  // Infer columns from data keys, with CDM column ordering
  const columns: Column[] = useMemo(() => {
    if (!hasData) return []
    const rawKeys = Object.keys(data[0])
    const orderedKeys = isCdmData ? sortCdmColumns(rawKeys) : rawKeys

    return orderedKeys.map((key) => {
      const { format, colorFn } = inferFormatter(key, data[0][key], !!isCdmData)
      return {
        key,
        label: formatColumnLabel(key),
        sortable: true,
        format,
        colorFn,
      }
    })
  }, [data, hasData, isCdmData])

  // Sort data
  const sortedData = useMemo(() => {
    if (!hasData) return []
    return [...data].sort((a, b) => {
      if (!sortKey || !sortDirection) return 0
      const aVal = a[sortKey]
      const bVal = b[sortKey]
      if (aVal == null && bVal == null) return 0
      if (aVal == null) return sortDirection === 'asc' ? 1 : -1
      if (bVal == null) return sortDirection === 'asc' ? -1 : 1
      let comparison: number
      if (typeof aVal === 'string' && typeof bVal === 'string') {
        comparison = aVal.localeCompare(bVal)
      } else if (typeof aVal === 'number' && typeof bVal === 'number') {
        comparison = aVal - bVal
      } else {
        comparison = String(aVal).localeCompare(String(bVal))
      }
      return sortDirection === 'asc' ? comparison : -comparison
    })
  }, [data, sortKey, sortDirection, hasData])

  const showManeuverButton = isCdmData && onPlanManeuver

  const selectedCdmId =
    selectedRowIndex !== null && sortedData[selectedRowIndex]
      ? getCdmId(sortedData[selectedRowIndex])
      : null

  const handleSort = (key: string) => {
    if (sortKey === key) {
      if (sortDirection === 'asc') {
        setSortDirection('desc')
      } else if (sortDirection === 'desc') {
        setSortKey(null)
        setSortDirection(null)
      }
    } else {
      setSortKey(key)
      setSortDirection('asc')
    }
  }

  const handleRowClick = (rowIndex: number) => {
    if (!showManeuverButton) return
    setSelectedRowIndex(selectedRowIndex === rowIndex ? null : rowIndex)
  }

  const handleExportCSV = () => {
    const csvContent = convertToCSV(data, columns)
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `${title || 'data'}-${new Date().toISOString().split('T')[0]}.csv`
    link.click()
    URL.revokeObjectURL(url)
  }

  const handlePlanManeuver = (cdmId: string) => {
    if (onPlanManeuver) {
      onPlanManeuver(cdmId)
    }
  }

  if (!hasData) {
    return (
      <div className="flex items-center justify-center h-full text-dark-400">
        <div className="text-center">
          <p className="text-sm">No data available</p>
          <p className="text-xs mt-2 text-dark-500">
            Data will appear here when generated by the agent
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* Header — actions on LHS so the primary CTA (PLAN MANEUVER) reads first */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          {showManeuverButton && (
            <PlanManeuverButton
              cdmId={selectedCdmId}
              onPlanManeuver={handlePlanManeuver}
              disabled={disabled}
            />
          )}
          <button
            onClick={handleExportCSV}
            className="inline-flex items-center gap-2 px-3 py-1.5 text-xs font-mono border border-retro-border text-retro-text bg-transparent rounded hover:bg-retro-border hover:text-retro-bg transition-colors"
            title="Export to CSV"
          >
            <Download size={14} />
            Export CSV
          </button>
        </div>
        <h3 className="text-sm font-medium text-dark-700">
          {title || 'Data Table'}
          <span className="ml-2 text-xs text-dark-500">
            ({data.length} row{data.length !== 1 ? 's' : ''})
          </span>
        </h3>
      </div>

      {/* Selection hint */}
      {showManeuverButton && (
        <p className="text-xs text-dark-500 italic">
          {selectedCdmId
            ? `Selected CDM: ${selectedCdmId}`
            : 'Click a row to select a CDM for maneuver planning'}
        </p>
      )}

      {/* Table */}
      <div className="overflow-x-auto border border-dark-200 rounded-lg">
        <table className="w-auto max-w-full text-sm">
          <thead className="bg-dark-100 border-b border-dark-200">
            <tr>
              {columns.map((column) => (
                <th
                  key={column.key}
                  className={`
                    px-4 py-3 text-left text-xs font-medium text-dark-700 uppercase tracking-wider
                    ${column.sortable ? 'cursor-pointer hover:bg-dark-150 select-none' : ''}
                  `}
                  onClick={column.sortable ? () => handleSort(column.key) : undefined}
                >
                  <div className="flex items-center gap-2">
                    <span>{column.label}</span>
                    {column.sortable && (
                      <span className="text-dark-500">
                        {sortKey === column.key ? (
                          sortDirection === 'asc' ? (
                            <ArrowUp size={14} />
                          ) : (
                            <ArrowDown size={14} />
                          )
                        ) : (
                          <ArrowUpDown size={14} />
                        )}
                      </span>
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-dark-50 divide-y divide-dark-200">
            {sortedData.map((row, rowIndex) => {
              const isSelected = selectedRowIndex === rowIndex
              const isSelectable = showManeuverButton
              const isEmergency = isCdmData && isEmergencyReportable(row)

              return (
                <tr
                  key={rowIndex}
                  onClick={() => handleRowClick(rowIndex)}
                  className={`
                    transition-colors
                    ${isSelectable ? 'cursor-pointer' : ''}
                    ${
                      isSelected
                        ? 'bg-retro-hover border-l-4 border-l-retro-amber'
                        : isEmergency
                          ? 'bg-red-500/10 border-l-4 border-l-red-500/50 hover:bg-red-500/20'
                          : 'hover:bg-dark-100'
                    }
                  `}
                >
                  {columns.map((column) => {
                    // Per "no color in table unless selected" QoL rule:
                    // ignore per-cell colorFn output unless this row is selected.
                    // Row-level emergency highlighting (left border + bg tint) is
                    // load-bearing safety signal and is preserved above.
                    return (
                      <td
                        key={column.key}
                        className={`
                          px-4 py-3 whitespace-nowrap font-mono text-xs
                          ${isSelected ? 'text-retro-amber' : 'text-dark-800'}
                        `}
                      >
                        {column.format
                          ? column.format(row[column.key])
                          : renderCellValue(row[column.key])}
                      </td>
                    )
                  })}
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}

export default DataTable
